"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ArrowRight, Sparkles, Leaf, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const timelineData = [{
  id: "stage-1",
  phase: "The Concept",
  title: "B+You",
  description: "Our journey began with a simple yet profound mission: to empower individuals to take control of their personal wellness. B+You laid the foundation for personalized care, focusing on the individual's unique needs and the initial steps toward a balanced life.",
  icon: Sparkles,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/Byou logo.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/Byou logo.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/Byou logo.jpg-wvc-sizes",
    alt: "Modern clinical wellness concept"
  },
  year: "2018"
}, {
  id: "stage-2",
  phase: "The Expansion",
  title: "Mo Self Care",
  description: "As our community grew, so did our vision. Mo Self Care expanded our offerings, introducing comprehensive self-care practices and creating a sanctuary for holistic healing. We integrated sauna and wellness rooms to deepen the restorative experience.",
  icon: Leaf,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/Mo logo round.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/luxury_sauna_wellness.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/luxury_sauna_wellness.png-wvc-sizes",
    alt: "Luxury sauna and wellness facility"
  },
  year: "2021"
}, {
  id: "stage-3",
  phase: "The System",
  title: "MO Protocol",
  description: "Today, we introduce MO Protocol—a high-end, systematic approach to wellness. We've integrated advanced medical services like IV therapy, vitamin injections, and lab testing with our luxury self-care foundation to optimize your health from the inside out.",
  icon: ShieldCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/mo protocol.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/mo protocol.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/mo protocol.png-wvc-sizes",
    alt: "Advanced IV therapy clinical precision"
  },
  year: "Present"
}];
function TimelineItem({
  item,
  index
}: {
  item: typeof timelineData[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  const Icon = item.icon;
  return <div data-index={index} ref={ref} className={`relative flex flex-col md:flex-row gap-8 md:gap-0 items-start w-full transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="absolute left-6 md:left-1/2 w-8 h-8 rounded-full bg-background border-4 border-primary flex items-center justify-center -translate-x-1/2 mt-6 z-10 shadow-md">
        <div className="w-2 h-2 rounded-full bg-[var(--color-green-accent)]" />
      </div>

      {}
      <div className={`w-full md:w-1/2 pl-16 md:pl-0 ${isEven ? 'md:pr-16' : 'md:pl-16 md:ml-auto'}`}>
        <Card className="bg-background/80 backdrop-blur-xl border border-border/50 shadow-lg overflow-hidden hover:shadow-xl hover:border-[var(--color-green-accent)]/30 transition-all duration-300">
          <AspectRatio ratio={16 / 9} className="bg-muted">
            <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} className="object-contain w-full h-full transition-transform duration-700 hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
            <div className="absolute bottom-4 left-6 flex items-center gap-2">
              <Badge variant="secondary" className="bg-[var(--color-gold-accent)] backdrop-blur-sm text-white border-none shadow-sm">
                {item.year}
              </Badge>
              <Badge variant="outline" className="bg-background/90 backdrop-blur-sm border-none shadow-sm flex items-center gap-1">
                <Icon className="w-3 h-3" />
                {item.phase}
              </Badge>
            </div>
          </AspectRatio>
          <CardHeader className="pt-6">
            <CardTitle className="font-default text-3xl tracking-tight text-foreground">
              {item.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground leading-relaxed">
              {item.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function TheEvolution() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="theevolution" className="py-24 md:py-32 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-accent/10 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-6xl">
        
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-4 tracking-widest uppercase text-xs">
            Our Journey
          </Badge>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-medium text-foreground mb-6">
            The Evolution of Wellness
          </h2>
          <p className="text-lg text-muted-foreground">
            From a personal mission to a comprehensive medical wellness ecosystem. Discover how MO Protocol has evolved to bring you the highest standard of care.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-border to-transparent md:-translate-x-1/2" />

          {}
          <div className="flex flex-col gap-16 md:gap-24">
            {timelineData.map((item, index) => <TimelineItem key={item.id} item={item} index={index} />)}
          </div>
        </div>

        {}
        <div className="mt-24 text-center">
          <Button asChild size="lg" className="rounded-full px-8 bg-[var(--color-green-accent)] hover:bg-[var(--color-green-accent)]/90 border-none">
            <Link to="/OurStoryPage">
              Read Our Full Story
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}